import {
  EMOJI_BY_TAG,
  ILinkItemInfoInsertion,
  IRichLogRecord,
  isEmojiTag,
} from "@ryde/shared";

/** Resolves `{{EMOJI_*}}` placeholders so i18n `{{reason}}` interpolation is not broken. */
export const resolveRichLogEmojiPlaceholders = (text: string): string =>
  text.replace(/\{\{([^}]+)\}\}/g, (match, paramKey: string) => {
    const key = paramKey.trim();
    if (isEmojiTag(key)) {
      return EMOJI_BY_TAG[key].emoji;
    }
    return match;
  });

export const PRICE_RATE_SEARCH_REMAINING_LINK_TAG = "remaining";
export const PRICE_RATE_STRIP_VISIBLE_ID_LIMIT = 16;

export type TPriceRateSearchIdStripKind = "remaining" | "filteredOut";

export interface IPriceRateSearchIdStrip {
  kind: TPriceRateSearchIdStripKind;
  entity: string;
  items: ILinkItemInfoInsertion[];
  reason?: string | null;
}

export const isPriceRateSearchIdStripTag = (tag: string): boolean =>
  tag === PRICE_RATE_SEARCH_REMAINING_LINK_TAG || tag.startsWith("filteredOut");

export const sanitizePriceRateSearchFilterReason = (
  raw: string | null | undefined,
): string | null => {
  if (!raw?.trim()) {
    return null;
  }
  const withoutIds = resolveRichLogEmojiPlaceholders(raw)
    .replace(/#\d+/g, " ")
    .replace(/\([^)]*\)/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[·,;:]+$/g, "")
    .trim();
  if (!withoutIds) {
    return null;
  }
  if (/^origin and destination areas$/i.test(withoutIds)) {
    return "origin/destination area";
  }
  if (withoutIds === withoutIds.toUpperCase()) {
    return withoutIds.toLowerCase();
  }
  return withoutIds;
};

const withLeadingLogEmoji = (
  normalizedMessage: string,
  reason: string | null,
): string | null => {
  if (!reason?.trim()) {
    return reason;
  }
  const leadingEmoji = normalizedMessage.match(
    /^(\p{Extended_Pictographic})\s+/u,
  )?.[1];
  if (!leadingEmoji || reason.startsWith(leadingEmoji)) {
    return reason;
  }
  return `${leadingEmoji} ${reason}`;
};

export const extractPriceRateSearchFilterReason = (
  message?: string | null,
): string | null => {
  if (!message) {
    return null;
  }
  const normalizedMessage = resolveRichLogEmojiPlaceholders(message);
  const finish = (raw: string | null | undefined): string | null =>
    withLeadingLogEmoji(
      normalizedMessage,
      sanitizePriceRateSearchFilterReason(raw),
    );
  const legacySqlMatch = normalizedMessage.match(/\bSQL ·\s+(.+?)\s+—/i);
  if (legacySqlMatch?.[1]) {
    return finish(legacySqlMatch[1]);
  }
  const filteringByMatch = normalizedMessage.match(/Filtering by\s+([^:]+):/i);
  if (filteringByMatch?.[1]) {
    return finish(filteringByMatch[1]);
  }
  const namedCheckMatch = normalizedMessage.match(
    /(?:\{\{EMOJI_[A-Z_]+\}\}\s*)*([A-Za-z][^—\n]{0,160}?\scheck(?:\s+skipped)?)\s+—/,
  );
  if (namedCheckMatch?.[1]) {
    return finish(namedCheckMatch[1]);
  }
  const excludedByMatch = normalizedMessage.match(/\bBY\s+(.+?)\s+·/i);
  if (excludedByMatch?.[1]) {
    return finish(excludedByMatch[1]);
  }
  const ruleDashMatch = normalizedMessage.match(
    /(?:^|[\s\p{Extended_Pictographic}])([A-Za-z][^—\n]{1,80}?)\s+—\s+(?:\d+\s*→|still\s+\d+|\d+\s+left|skipped)/u,
  );
  if (ruleDashMatch?.[1]) {
    return finish(ruleDashMatch[1]);
  }
  return null;
};

export const extractPriceRateSearchIdStrips = (
  record: IRichLogRecord,
): IPriceRateSearchIdStrip[] => {
  const linkItems = record.messageParams?.linkItems;
  if (!linkItems) {
    return [];
  }

  const remainingItems: ILinkItemInfoInsertion[] = [];
  let remainingEntity = "priceRate";
  const filteredOutItems: ILinkItemInfoInsertion[] = [];
  let filteredOutEntity = "priceRate";

  for (const [tag, group] of Object.entries(linkItems)) {
    if (!group.linkItems?.length) {
      continue;
    }
    if (tag === PRICE_RATE_SEARCH_REMAINING_LINK_TAG) {
      remainingItems.push(...group.linkItems);
      remainingEntity = group.entity;
    } else if (tag.startsWith("filteredOut")) {
      filteredOutItems.push(...group.linkItems);
      filteredOutEntity = group.entity;
    }
  }

  if (!remainingItems.length && !filteredOutItems.length) {
    return [];
  }

  const reason = extractPriceRateSearchFilterReason(record.message);

  return [
    {
      kind: "filteredOut",
      entity: filteredOutEntity,
      items: filteredOutItems,
      reason,
    },
    {
      kind: "remaining",
      entity: remainingEntity,
      items: remainingItems,
    },
  ];
};

export const slicePriceRateStripItems = ({
  items,
  expanded,
  limit = PRICE_RATE_STRIP_VISIBLE_ID_LIMIT,
}: {
  items: ILinkItemInfoInsertion[];
  expanded: boolean;
  limit?: number;
}): { visibleItems: ILinkItemInfoInsertion[]; hiddenCount: number } => {
  if (expanded || items.length <= limit) {
    return { visibleItems: items, hiddenCount: 0 };
  }
  return {
    visibleItems: items.slice(0, limit),
    hiddenCount: items.length - limit,
  };
};

export const formatRichLogLinkAnchorText = ({
  entity,
  item,
  idOnly,
}: {
  entity: string;
  item: { id: number; name?: string | null };
  idOnly?: boolean;
}): string => {
  if (idOnly) {
    return String(item.id);
  }
  const name = item.name ?? "";
  if (!name) {
    return String(item.id);
  }
  const idAlreadyInName =
    (entity === "priceRate" || entity === "supplierPriceRate") &&
    name.includes(`price rate ${item.id}`);
  if (idAlreadyInName) {
    return name;
  }
  return `${name} (${item.id})`;
};
